"use client";

import { motion } from "motion/react";
import { useState, useEffect } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Solar icons by 480 Design (CC BY 4.0)
const SolarGraduationCap = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" {...props}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M9.464 2.82a5.92 5.92 0 0 1 5.072 0l6.69 3.138C22.28 6.45 22.75 7.522 22.75 8.5s-.47 2.05-1.523 2.543l-1.477.692v4.89c0 1.253-.628 2.463-1.772 3.097c-.752.416-1.74.917-2.78 1.316c-1.027.394-2.164.712-3.198.712s-2.171-.318-3.199-.712c-1.04-.399-2.027-.9-2.779-1.316c-1.144-.634-1.772-1.844-1.772-3.097v-4.89l-1.477-.693l-.023-.01V14a.75.75 0 0 1-1.5 0V8.5c0-.978.471-2.05 1.523-2.542zM5.75 12.439v4.187c0 .763.379 1.441.999 1.785a21 21 0 0 0 2.59 1.227c.963.37 1.899.613 2.661.613s1.698-.243 2.662-.613a21 21 0 0 0 2.59-1.227c.62-.344.998-1.022.998-1.785v-4.187l-3.714 1.741a5.92 5.92 0 0 1-5.072 0zM3.41 9.684c-.403-.189-.66-.64-.66-1.184s.257-.995.66-1.184l6.691-3.137a4.42 4.42 0 0 1 3.798 0l6.691 3.137c.403.189.66.64.66 1.184s-.257.995-.66 1.184l-6.691 3.137a4.42 4.42 0 0 1-3.798 0z"
            clipRule="evenodd"
        />
    </svg>
);

const SolarFire = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" {...props}>
        <g fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M20 13.111C20 20.222 13.956 22 10.933 22C8.29 22 3 20.222 3 13.111c0-2.782 1.461-4.65 2.86-5.716c.778-.594 1.77-.003 1.87.971l.086.838c.105 1.02 1.033 1.857 1.893 1.298C11.394 9.407 12 6.775 12 5.333V5.01c0-1.43 1.444-2.35 2.602-1.512C17.165 5.35 20 8.584 20 13.11Z" />
            <path d="M8 18.445C8 21.289 10.489 22 11.733 22c1.09 0 3.267-.711 3.267-3.555c0-1.102-.59-1.845-1.16-2.274c-.398-.299-.957-.03-1.094.449c-.178.624-.823 1.016-1.152.456c-.3-.512-.3-1.28-.3-1.743c0-.636-.64-1.048-1.155-.674C9.106 15.409 8 16.68 8 18.445Z" />
        </g>
    </svg>
);

const SolarMicrophone = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" {...props}>
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M6.25 8a5.75 5.75 0 1 1 11.5 0v3a5.75 5.75 0 0 1-11.5 0zM12 3.75A4.25 4.25 0 0 0 7.75 8v3a4.25 4.25 0 0 0 8.5 0V8A4.25 4.25 0 0 0 12 3.75M10.25 8a.75.75 0 0 1 .75-.75h2a.75.75 0 0 1 0 1.5h-2a.75.75 0 0 1-.75-.75M4 9.25a.75.75 0 0 1 .75.75v1a7.25 7.25 0 1 0 14.5 0v-1a.75.75 0 0 1 1.5 0v1a8.75 8.75 0 0 1-8 8.718V22a.75.75 0 0 1-1.5 0v-2.282a8.75 8.75 0 0 1-8-8.718v-1A.75.75 0 0 1 4 9.25M9.25 11a.75.75 0 0 1 .75-.75h4a.75.75 0 0 1 0 1.5h-4a.75.75 0 0 1-.75-.75"
            clipRule="evenodd"
        />
    </svg>
);

const tiers = [
    {
        key: "courses",
        icon: SolarGraduationCap,
        accent: "from-amber-500 to-primary",
        href: "/courses",
    },
    {
        key: "accelerator",
        icon: SolarFire,
        accent: "from-indigo-500 to-violet-600",
        href: "/accelerators/rocket",
        featured: true,
    },
    // Masquée tant que MENTORING_IN_NAVIGATION est à false (next.config.mjs) :
    // la grille juste en dessous suit le nombre de cartes restantes.
    ...(process.env.MENTORING_IN_NAVIGATION
        ? [
              {
                  key: "mentoring",
                  icon: SolarMicrophone,
                  accent: "from-emerald-500 to-teal-600",
                  href: "/mentoring",
              },
          ]
        : []),
];

export default function SupportLevels({ prices }) {
    const t = useTranslations();
    const locale = useLocale();
    const [isAfricaPricing, setIsAfricaPricing] = useState(false);

    useEffect(() => {
        fetch("/api/geo/country")
            .then((res) => res.json())
            .then((data) => {
                if (data?.isAfricaPricing) setIsAfricaPricing(true);
            })
            .catch(() => {
                // Échec silencieux : on garde le tarif standard, déjà affiché.
            });
    }, []);

    // Le montant vient du serveur, calculé avec la même règle que la page des
    // tarifs (lib/pricing.js) : la page d'accueil annonçait « Dès 29 €/mois »
    // alors qu'aucun tarif ne valait 29 €.
    const formatPrice = (value) =>
        typeof value === "number"
            ? value.toLocaleString(locale, { maximumFractionDigits: 2 })
            : null;

    const coursesPrice = isAfricaPricing
        ? formatPrice(prices?.africa)
        : formatPrice(prices?.standard);

    const renderPrice = (tierKey) => {
        if (tierKey !== "courses") {
            return t(`SupportLevels_${tierKey}_price`);
        }
        if (!coursesPrice) return null;
        return t(
            isAfricaPricing
                ? "SupportLevels_courses_price_africa"
                : "SupportLevels_courses_price",
            { price: coursesPrice },
        );
    };

    return (
        <section className="relative max-w-7xl mx-auto px-5 xl:px-0 mt-32">
            {/* Header */}
            <div className="text-center">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.5 }}
                    className="rounded-full border border-primary-100 px-4 py-1 uppercase font-light inline text-sm text-primary-100"
                >
                    {t("SupportLevels_kicker")}
                </motion.div>

                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                    className="dark:text-white md:text-5xl sm:text-4xl text-3xl font-bold lg:px-10 mt-3 sm:leading-relaxed"
                    dangerouslySetInnerHTML={{
                        __html: t.raw("SupportLevels_lead"),
                    }}
                />

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6, delay: 0.25 }}
                    className="mt-5 dark:text-textSecondDark lg:w-7/12 mx-auto"
                    style={{ lineHeight: "170%" }}
                >
                    {t("SupportLevels_description")}
                </motion.p>
            </div>

            {/* Three tiers */}
            <div
                className={`mt-14 grid grid-cols-1 gap-5 ${
                    tiers.length >= 3 ? "md:grid-cols-3" : "md:grid-cols-2"
                }`}
            >
                {tiers.map((tier, index) => {
                    const Icon = tier.icon;
                    return (
                        <motion.div
                            key={tier.key}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{
                                duration: 0.6,
                                delay: index * 0.12,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            whileHover={{ y: -8 }}
                            className={`group relative flex flex-col overflow-hidden rounded-3xl border ${
                                tier.featured
                                    ? "border-primary/40 bg-white dark:bg-bgDark-500 shadow-lg shadow-primary/10"
                                    : "border-gray-200 dark:border-grayDark-800 bg-white dark:bg-bgDark-500"
            } p-6 md:p-8 transition-all duration-300 ease-out hover:border-primary/60 hover:shadow-2xl hover:shadow-primary/15`}
                        >
                            {/* Subtle gradient overlay au hover */}
                            <div
                                aria-hidden
                                className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${tier.accent} opacity-0 transition-opacity duration-500 group-hover:opacity-[0.04]`}
                            />

                            {/* Accent line top au hover */}
                            <div
                                aria-hidden
                                className={`pointer-events-none absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${tier.accent} origin-left scale-x-0 transition-transform duration-500 ease-out group-hover:scale-x-100`}
                            />

                            {/* Featured ribbon */}
                            {tier.featured && (
                                <div className="absolute top-4 right-4 z-10">
                                    <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-primary bg-primary/10 rounded-full px-2.5 py-1">
                                        {t("SupportLevels_featured_badge")}
                                    </span>
                                </div>
                            )}

                            {/* Icon */}
                            <div
                                className={`relative inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${tier.accent} text-white shadow-md transition-all duration-500 ease-out group-hover:scale-110 group-hover:-rotate-3 group-hover:shadow-xl`}
                            >
                                <Icon className="w-7 h-7" />
                            </div>

                            {/* Title */}
                            <h3 className="relative mt-6 font-sourceSerif text-2xl text-gray-900 dark:text-white tracking-tight transition-colors duration-300 group-hover:text-primary">
                                {t(`SupportLevels_${tier.key}_title`)}
                            </h3>

                            {/* Tagline */}
                            <p className="relative mt-1 text-sm font-medium text-primary">
                                {t(`SupportLevels_${tier.key}_tagline`)}
                            </p>

                            {/* Description */}
                            <p className="relative mt-4 text-[15px] text-gray-600 dark:text-grayDark-400 leading-relaxed">
                                {t(`SupportLevels_${tier.key}_description`)}
                            </p>

                            {/* Divider */}
                            <div className="relative my-6 h-px bg-gray-100 dark:bg-grayDark-800" />

                            {/* Meta */}
                            <ul className="relative space-y-2.5 text-sm">
                                <li className="flex items-start gap-2">
                                    <span className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-gray-400 dark:text-grayDark-600 w-16 shrink-0">
                                        {t("SupportLevels_meta_for")}
                                    </span>
                                    <span className="text-gray-700 dark:text-grayDark-300">
                                        {t(`SupportLevels_${tier.key}_for`)}
                                    </span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-gray-400 dark:text-grayDark-600 w-16 shrink-0">
                                        {t("SupportLevels_meta_format")}
                                    </span>
                                    <span className="text-gray-700 dark:text-grayDark-300">
                                        {t(`SupportLevels_${tier.key}_format`)}
                                    </span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-gray-400 dark:text-grayDark-600 w-16 shrink-0">
                                        {t("SupportLevels_meta_price")}
                                    </span>
                                    <span className="text-gray-700 dark:text-grayDark-300 font-medium">
                                        {renderPrice(tier.key)}
                                    </span>
                                </li>
                            </ul>

                            {/* Ce qu'un compte gratuit donne déjà. La carte
                                affiche un prix mensuel juste au-dessus : sans
                                cette ligne, le visiteur en conclut qu'il ne
                                peut rien voir avant de payer. */}
                            {tier.key === "courses" && (
                                <p className="relative mt-4 mb-0 rounded-xl bg-primary/5 px-3 py-2 text-[13px] leading-relaxed text-gray-600 dark:text-grayDark-400">
                                    {t("SupportLevels_courses_free_hint")}
                                </p>
                            )}

                            {/* CTA */}
                            <Link
                                href={tier.href}
                                className={`relative mt-auto pt-6 inline-flex items-center gap-2 text-sm font-semibold transition-colors duration-300 ${
                                    tier.featured
                                        ? "text-primary group-hover:text-primary"
                                        : "text-gray-900 dark:text-white group-hover:text-primary dark:group-hover:text-primary"
                                }`}
                            >
                                {t(`SupportLevels_${tier.key}_cta`)}
                                <SolarIcon name="arrow-right-linear" className="w-4 h-4 transition-transform duration-300 ease-out group-hover:translate-x-2" />
                            </Link>
                        </motion.div>
                    );
                })}
            </div>
        </section>
    );
}
